import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RecentPostsSidebar from '../components/RecentPostsSidebar';
import { parseMarkdown } from '../utils/markdownParser';

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
      if (error.response?.status === 404) {
        navigate('/events');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return { date: dateStr, time: timeStr };
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <h2>Event not found</h2>
          <Link to="/events" className="btn btn-primary mt-3">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const { date, time } = formatDateTime(event.event_date);

  return (
    <>
      {/* Page Header */}
      <div
        className="container-fluid"
        style={{
          background: 'linear-gradient(rgba(var(--tgh-blue-rgb), 0.9), rgba(var(--tgh-blue-rgb), 0.9)), url(/img/hospital-building.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '4rem 0 3rem',
        }}
      >
        <div className="container">
          <div className="text-center">
            <h2 className="text-white mb-3" style={{ fontSize: '2.5rem', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif", textTransform: 'uppercase', letterSpacing: '1px' }}>
              TGH Events
            </h2>
            <nav aria-label="breadcrumb" className="mt-3 d-flex justify-content-center">
              <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/events" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                    Events
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white" aria-current="page">
                  Event Details
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Event Content */}
      <section className="py-5" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="row tgh-page-with-sidebar">
            <div className="col-xl-9 col-lg-8 col-12">
              <article className="tgh-news-article">
                {/* Status Badge */}
                <span className={`badge ${event.status === 'upcoming' ? 'bg-success' : 'bg-secondary'} mb-3`} style={{ fontSize: '0.85rem' }}>
                  {event.status === 'upcoming' ? 'Upcoming' : 'Past Event'}
                </span>

                {/* Title */}
                <h1 className="display-4 mb-4" style={{ color: '#1a1a1a', fontWeight: '600', fontFamily: "'Lora', 'Georgia', serif" }}>
                  {event.title}
                </h1>

                {/* Event Details */}
                <div className="mb-4 p-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="bi bi-calendar3 text-primary" style={{ fontSize: '1.2rem' }}></i>
                        <div>
                          <strong style={{ display: 'block', fontSize: '0.9rem', color: '#6c757d' }}>Date</strong>
                          <span style={{ fontSize: '1rem', color: '#1a1a1a' }}>{date}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="bi bi-clock text-primary" style={{ fontSize: '1.2rem' }}></i>
                        <div>
                          <strong style={{ display: 'block', fontSize: '0.9rem', color: '#6c757d' }}>Time</strong>
                          <span style={{ fontSize: '1rem', color: '#1a1a1a' }}>{time}</span>
                        </div>
                      </div>
                    </div>
                    {event.location && (
                      <div className="col-12">
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-geo-alt text-primary" style={{ fontSize: '1.2rem' }}></i>
                          <div>
                            <strong style={{ display: 'block', fontSize: '0.9rem', color: '#6c757d' }}>Location</strong>
                            <span style={{ fontSize: '1rem', color: '#1a1a1a' }}>{event.location}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description + optional featured image (floated so text wraps, like magazine layout) */}
                <div
                  className="event-description tgh-news-post-body"
                  style={{
                    fontSize: '1.1rem',
                    lineHeight: '1.8',
                    color: '#333',
                    marginBottom: '3rem',
                    overflow: 'visible',
                    textAlign: 'justify',
                  }}>
                  {event.image && (
                    <img
                      src={event.image.startsWith('/uploads/') ? event.image : `/uploads/${event.image}`}
                      alt={event.title}
                      className="event-featured-image-float"
                      loading="eager"
                      decoding="async"
                      onError={(e) => {
                        e.target.src = '/img/Gallery/opd1.jpg';
                      }}
                    />
                  )}
                  <div
                    className="event-description-html"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(event.description) }}
                  />
                </div>
              </article>
            </div>
            
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4 col-12 tgh-sidebar-col">
              <div style={{ position: 'sticky', top: '20px', zIndex: 10 }}>
                <RecentPostsSidebar type="events" limit={5} excludeId={id} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default EventDetail;
